import { Grid, SkeletonText, Stack, Typography } from '@optimus-web/core';
import { ActionsMenu, CardContainer, HighlightScore, HighlightScoreProps, TitleStack } from '../common';
import { ChartState } from '../common/components/ChartState';
import { ProgressChart, ProgressChartProps } from '../ProgressChart';
import { BaseCardProps } from '../types';

export interface ProgressCharts extends Omit<ProgressChartProps, 'empty' | 'error'> {
    id: string;
    score?: HighlightScoreProps;
    showTitle?: boolean;
}

interface ChartsColumns {
    desktop?: number;
    tablet?: number;
    phone?: number;
}

export interface ProgressChartCardProps extends BaseCardProps {
    charts: ProgressCharts[];
    columns?: ChartsColumns;
}

export const ProgressChartCard = ({
    title,
    subtitle,
    menuActions,
    id,
    charts,
    columns = {
        desktop: 1,
        tablet: 1,
        phone: 1,
    },
    loading,
    error,
    empty,
    ...other
}: ProgressChartCardProps) => {
    const displayChartState = error || empty;

    return (
        <CardContainer {...other}>
            <Stack vertical spacing="50">
                <Stack itemAlignment="start" distribution="space-between" spacing="200">
                    <TitleStack vertical fill>
                        {loading && !title ? <SkeletonText width="66%" /> : null}
                        {title ? (
                            <Stack itemAlignment="center" spacing="100">
                                <Typography textStyle="bodyLargeStrong" ellipsis as="div">
                                    {title}
                                </Typography>
                            </Stack>
                        ) : null}
                        {subtitle && !loading ? (
                            <Typography textStyle="bodySmall" ellipsis maxLines={2} variant="tertiary" as="div">
                                {subtitle}
                            </Typography>
                        ) : null}
                    </TitleStack>
                    {menuActions ? (
                        <Stack spacing="50">
                            <ActionsMenu actions={menuActions} id={id} />
                        </Stack>
                    ) : null}
                </Stack>
            </Stack>
            {displayChartState ? <ChartState empty={empty} error={error} /> : null}
            {!displayChartState ? (
                <Grid
                    justifyContent="space-between"
                    gap="200"
                    alignItems="center"
                    largePhoneMax={{
                        templateColumns: `repeat(${columns.phone}, 1fr)`,
                    }}
                    tabletOnly={{
                        templateColumns: `repeat(${columns.tablet}, 1fr)`,
                    }}
                    templateColumns={`repeat(${columns.desktop}, 1fr)`}
                    height="100%"
                >
                    {charts.map(
                        ({
                            showTitle = true,
                            score,
                            title: chartTitle,
                            data,
                            id: chartId,
                            total,
                            animated = true,
                            legend,
                            labelFormatter,
                            valueFormatter,
                        }) => (
                            <Stack
                                key={`${id}-${chartId || chartTitle}`}
                                vertical
                                fill
                                spacing={showTitle ? '25' : '150'}
                            >
                                {!loading && score ? <HighlightScore {...score} /> : null}
                                <Stack spacing="150" vertical fill>
                                    {!loading && showTitle && chartTitle ? (
                                        <Typography textStyle="bodySmall" as="div">
                                            {chartTitle}
                                        </Typography>
                                    ) : null}
                                    {loading && showTitle && chartTitle ? <SkeletonText width="66%" /> : null}
                                    <ProgressChart
                                        data={data}
                                        title={chartTitle || title}
                                        id={`${id}-${chartId || chartTitle}`}
                                        total={total}
                                        animated={animated}
                                        legend={legend}
                                        labelFormatter={labelFormatter}
                                        valueFormatter={valueFormatter}
                                        loading={loading}
                                    />
                                </Stack>
                            </Stack>
                        )
                    )}
                </Grid>
            ) : null}
        </CardContainer>
    );
};
